/**
 * Product Hub — the vocabulary the whole module shares.
 *
 * Two things live here and nothing else: the `Effective` shape that every inheritance-aware
 * resolver returns, and the code/label pairs that are not specific to one domain file.
 * Per-domain codes (product types, inventory modes, shipping targets…) live next to the resolver
 * that branches on them — `productHub.catalog.ts`, `productHub.inventory.ts`, and so on.
 *
 * No React, no MUI, no browser globals: the backend imports this file directly through the
 * `@malash-office/types/product-hub` alias.
 *
 * ## Why the codes are English while the rest of the repo stores Hebrew labels
 *
 * Elsewhere in this codebase an enum column stores its own Hebrew label, so a row renders and
 * exports as-is. Product Hub deviates, deliberately (ADR-017):
 *
 *  1. These values end up inside Postgres CHECK constraints. A Hebrew literal in a
 *     `CHECK (...)` clause is unreviewable in a migration diff and impossible to grep for.
 *  2. They cross the wire to WooCommerce/Shopify. External APIs take `physical`, not `פיזי`.
 *  3. `status`, `type` and `mode` recur across eight tables here. Hebrew labels would collide —
 *     `פעיל` already means three different things in this module alone.
 *
 * The Hebrew never disappears; it moves into the `*_OPTIONS` maps below and in the domain files,
 * which stay the single source for both the UI and Excel export.
 */

/** A label for a code, in the shape the design system's `Select`/`FilterConfig` already take. */
export interface PhOption<TValue extends string = string> {
  value: TValue;
  label: string;
}

/** Build the option list for a code array from its label map, preserving the array's order. */
export const phOptions = <T extends string>(codes: readonly T[], labels: Record<T, string>): PhOption<T>[] =>
  codes.map(value => ({ value, label: labels[value] }));

/**
 * A value the system inherits, together with where it came from.
 *
 * The architecture spec (§53) makes this an official pattern rather than a convenience: the
 * frontend is not allowed to decide between a central value and an override, so every resolver
 * hands back both inputs, the answer, and the reason. `sourceLabel` is written by the server for
 * the same reason — the set of sources grows, and a UI that derives its own explanation from
 * `source` silently goes stale when it does.
 */
export interface Effective<TValue, TSource extends string> {
  /** The central value, before any override. Null when there is none to inherit. */
  base: TValue | null;
  /** The override, or null when nothing overrides. Never a copy of `base`. */
  override: TValue | null;
  /** What the system actually uses. */
  value: TValue | null;
  /** Which input won. */
  source: TSource;
  /** Hebrew explanation for the UI, e.g. "יורש מהמחיר המרכזי". */
  sourceLabel: string;
}

/**
 * Resolve base + override into an `Effective`.
 *
 * `undefined` and `null` both mean "not set" — a caller reading an optional column should not
 * have to normalise first. `0`, `''` and `false` are real overrides and are kept.
 */
export const resolveEffective = <TValue, TSource extends string>(
  base: TValue | null | undefined,
  override: TValue | null | undefined,
  sources: {
    whenOverride: TSource;
    whenBase: TSource;
    whenMissing: TSource;
    labels: Record<TSource, string>;
  }
): Effective<TValue, TSource> => {
  const baseValue = base ?? null;
  const overrideValue = override ?? null;

  const source =
    overrideValue !== null ? sources.whenOverride : baseValue !== null ? sources.whenBase : sources.whenMissing;

  return {
    base: baseValue,
    override: overrideValue,
    value: overrideValue !== null ? overrideValue : baseValue,
    source,
    sourceLabel: sources.labels[source]
  };
};

/* ------------------------------------------------------------------ */
/* Statuses                                                            */
/* ------------------------------------------------------------------ */

/** The lifecycle every catalog entity shares. Soft delete is `status` + `archivedAt`, never a row deletion. */
export const PH_ENTITY_STATUSES = ['active', 'inactive'] as const;
export type PhEntityStatus = (typeof PH_ENTITY_STATUSES)[number];

export const PH_ENTITY_STATUS_LABELS: Record<PhEntityStatus, string> = {
  active: 'פעיל',
  inactive: 'לא פעיל'
};

export const PH_ENTITY_STATUS_OPTIONS = phOptions(PH_ENTITY_STATUSES, PH_ENTITY_STATUS_LABELS);

/* ------------------------------------------------------------------ */
/* Currency                                                            */
/* ------------------------------------------------------------------ */

/**
 * The currencies a store may be configured in.
 *
 * There is no conversion anywhere in this module — the spec (§14, §18, §44) forbids it. A store
 * in USD simply requires an explicit `StorePrice`; it never gets an ILS base price converted for
 * it. This list therefore only has to cover what stores are actually opened in.
 */
export const PH_CURRENCIES = ['ILS', 'USD', 'EUR', 'GBP'] as const;
export type PhCurrency = (typeof PH_CURRENCIES)[number];

export const PH_CURRENCY_LABELS: Record<PhCurrency, string> = {
  ILS: 'שקל (₪)',
  USD: 'דולר ($)',
  EUR: 'אירו (€)',
  GBP: 'ליש"ט (£)'
};

export const PH_CURRENCY_OPTIONS = phOptions(PH_CURRENCIES, PH_CURRENCY_LABELS);

export const PH_DEFAULT_CURRENCY: PhCurrency = 'ILS';

/* ------------------------------------------------------------------ */
/* Units                                                               */
/* ------------------------------------------------------------------ */

/**
 * Dimensions and weights are stored as numbers plus a unit code, never as text.
 * "30x20x10" and "2.4 ק\"ג" are display strings; they are not a source of truth (spec §12).
 */
/**
 * Metric only, and only the three lengths a catalogue in Israel actually uses.
 *
 * `in` and `lb` were dropped on request: nothing in the module converts between units, so every
 * extra one is a way for two products to be described in figures that cannot be compared — and a
 * shipping rule summing 30 (cm) and 12 (in) produces a number that is wrong with no error anywhere.
 *
 * These option lists are **the** source for both apps. They were copied literally into two wizard
 * section files, which is how `m` came to be offered by the form and rejected by the API.
 */
export const PH_DIMENSION_UNITS = ['m', 'cm', 'mm'] as const;
export type PhDimensionUnit = (typeof PH_DIMENSION_UNITS)[number];

export const PH_DIMENSION_UNIT_LABELS: Record<PhDimensionUnit, string> = {
  m: 'מטר',
  cm: 'ס"מ',
  mm: 'מ"מ'
};

export const PH_DIMENSION_UNIT_OPTIONS = phOptions(PH_DIMENSION_UNITS, PH_DIMENSION_UNIT_LABELS);

export const PH_WEIGHT_UNITS = ['kg', 'g'] as const;
export type PhWeightUnit = (typeof PH_WEIGHT_UNITS)[number];

export const PH_WEIGHT_UNIT_LABELS: Record<PhWeightUnit, string> = {
  kg: 'ק"ג',
  g: 'גרם'
};

export const PH_WEIGHT_UNIT_OPTIONS = phOptions(PH_WEIGHT_UNITS, PH_WEIGHT_UNIT_LABELS);

/* ------------------------------------------------------------------ */
/* Audit sources                                                       */
/* ------------------------------------------------------------------ */

/** Who caused a write. Mirrors `CustomerActivity.source`, extended for the sync worker. */
export const PH_AUDIT_SOURCES = ['manual', 'import', 'job', 'sync'] as const;
export type PhAuditSource = (typeof PH_AUDIT_SOURCES)[number];

export const PH_AUDIT_SOURCE_LABELS: Record<PhAuditSource, string> = {
  manual: 'עריכה ידנית',
  import: 'ייבוא',
  job: 'תהליך מתוזמן',
  sync: 'סנכרון חנות'
};

export const PH_AUDIT_SOURCE_OPTIONS = phOptions(PH_AUDIT_SOURCES, PH_AUDIT_SOURCE_LABELS);
